import { useEffect } from 'react';
import { canUsePointerFx } from './useMotionPrefs';

// macOS-dock style magnification: every `.dock-item` inside the container gets a `--dock-scale`
// based on its horizontal distance to the cursor (gaussian falloff). CSS handles the spring transition.
const useDockMagnify = (ref, { maxScale = 1.26, spread = 1.35 } = {}) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || !canUsePointerFx()) return undefined;

        let frame = 0;
        let pointerX = null;

        const apply = () => {
            frame = 0;
            // Screen px per layout px (the hero artboard itself is scaled to fit its column)
            const ratio = el.offsetWidth ? el.getBoundingClientRect().width / el.offsetWidth : 1;
            el.querySelectorAll('.dock-item').forEach(item => {
                let scale = 1;
                if (pointerX !== null) {
                    // Items scale from their bottom-centre, so the rect centre is stable while magnified
                    const rect = item.getBoundingClientRect();
                    const unit = Math.max(item.offsetWidth, 26) * ratio;
                    const distance = (pointerX - (rect.left + rect.width / 2)) / (unit * spread);
                    scale = 1 + (maxScale - 1) * Math.exp(-(distance * distance));
                }
                item.style.setProperty('--dock-scale', scale.toFixed(3));
            });
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(apply); };

        const onMove = (e) => {
            if (e.pointerType !== 'mouse') return;
            pointerX = e.clientX;
            schedule();
        };
        const onLeave = () => { pointerX = null; schedule(); };

        el.addEventListener('pointermove', onMove, { passive: true });
        el.addEventListener('pointerleave', onLeave);
        return () => {
            cancelAnimationFrame(frame);
            el.removeEventListener('pointermove', onMove);
            el.removeEventListener('pointerleave', onLeave);
        };
    }, [ref, maxScale, spread]);
};

export default useDockMagnify;
